import React, {Fragment, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Link, Redirect} from 'react-router-dom';
import {Calendar, momentLocalizer, Views} from 'react-big-calendar';
import {FaBan, FaCalendarAlt, FaCalendarCheck, FaCheckCircle, FaClipboardList, FaLock, FaPen, FaQuestionCircle} from 'react-icons/fa';
import moment from 'moment';
import 'moment/locale/it';
import 'moment/locale/en-gb';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {Button, Fade, Modal, Row} from 'react-bootstrap';
import Dialog from 'react-bootstrap-dialog';
import CommonMetaTag from './common-meta-tag';
import ServiceSlot from './service-slot';
import {calendarMessages} from '../constants';
import {useTranslation} from 'react-i18next';
import PartnerPlanningGuideModal from './partner-planning-guide-modal';
import SkyworkPlannerToolbar from './skywork-planner-toolbar';

const ServiceCalendar = ({profile, ownedService, ownedServices, isLogged, ownedFieldSlots, editSlot, newSlot, isSlotModalVisible, hideSlotModal, ownedFieldSlot, saveSlot, deleteSlot, getSlotsByWeek, selectService}) => {
  const {t, i18n} = useTranslation();
  const [showPlanningGuide, setShowPlanningGuide] = useState(false);
  const [view, setView] = useState(Views.WEEK);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [planningSelection, setPlanningSelection] = useState(null);
  useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(new Date()), 60000);
    return () => window.clearInterval(timer);
  }, []);
  if (!isLogged) {
    return (
      <Redirect to="/login"/>
    );
  }

  if (!ownedService) {
    return <Redirect to="/partner-fields"/>;
  }

  const localizer = momentLocalizer(moment);
  const events = ownedFieldSlots.map(slot => {
    return {
      id: slot.slotId,
      title: slot.state === 'reserved' ? `${slot.videoSellPermission ? '[video]' : ''} ${slot.description}` : slot.state === 'unavailable' ? `${slot.description}` : slot.bookingId ? '' : slot.presetPlaces > 1 ? t('EVENT_DESCRIPTION_PLACES', {description: slot.description, places: slot.availablePlaces}) : slot.price > 0 ? t('EVENT_DESCRIPTION', {price: slot.price}) : t('AVAILABLE'),
      start: new Date(slot.startDatetime),
      end: new Date(slot.endDatetime),
      slot
    };
  });

  let dialog = null;
  const onRemove = values => {
    dialog.show({
      title: t('CONFIRM'),
      body: t('EVENT_DELETE_CONFIRM'),
      actions: [
        Dialog.Action(
          t('DELETE'),
          () => deleteSlot(values),
          'btn btn-secondary'
        ),
        Dialog.Action(
          t('NO'),
          null,
          'btn btn_red_outline_rp'
        )
      ],
      bsSize: 'small',
      onHide: dialog => {
        dialog.hide();
      }
    });
  };

  const minTime = new Date();
  minTime.setHours(7, 0, 0);
  const maxTime = new Date();
  maxTime.setHours(23, 58, 59);

  const eventStyleGetter = event => {
    const backgroundColor = event.slot.state === 'reserved' ? event.slot.color ? event.slot.color : '#ADB5BD' : event.slot.state === "unavailable" ? '#444444' : event.slot.bookingId ? '#3598DB' : '#31BC8C';
    const color = '#fff';
    const style = {
      backgroundColor,
      borderRadius: '5px',
      opacity: 1,
      color,
      border: '1px',
      borderStyle: 'solid',
      borderColor: '#00152F',
      display: 'block'
    };
    let stateClass = 'single';
    if (event.slot.state === 'reserved') stateClass = 'reserved';
    else if (event.slot.state === 'unavailable') stateClass = 'unavailable';
    else if (event.slot.bookingId) stateClass = 'booked';
    else if (event.slot.presetPlaces > 1) stateClass = 'places';
    return {className: `booking-calendar-slot booking-calendar-slot--${stateClass}`, style};
  };

  const CalendarEvent = ({event}) => {
    let label = t('AVAILABLE');
    let Icon = FaCalendarAlt;
    if (event.slot.state === 'reserved') { label = event.slot.description || t('BOOKING_SLOT_RESERVED'); Icon = FaLock; }
    else if (event.slot.state === 'unavailable') { label = event.slot.description || t('BOOKING_SLOT_UNAVAILABLE'); Icon = FaBan; }
    else if (event.slot.bookingId) { label = t('BOOKED'); Icon = FaCheckCircle; }
    else if (event.slot.presetPlaces > 1) { label = t('EVENT_PLACES', {places: event.slot.availablePlaces}); Icon = FaClipboardList; }
    return (
      <div className="booking-calendar-slot-content">
        <span className="booking-calendar-slot-status"><Icon/>{label}</span>
        <span className="booking-calendar-slot-title">{event.title}</span>
      </div>
    );
  };

  const Toolbar = toolbarProps => (
    <SkyworkPlannerToolbar
      {...toolbarProps}
      view={view}
      fields={ownedServices}
      selectedFieldId={ownedService.id}
      onFieldChange={serviceId => {
        const nextService = ownedServices.find(service => String(service.id) === String(serviceId));
        if (nextService) selectService(nextService, calendarDate);
      }}
      allFieldsLabel={null}
      chooseFieldLabel={t('SELECT_SERVICE')}
    />
  );

  const handleNavigate = targetDate => {
    setCalendarDate(targetDate);
    getSlotsByWeek(ownedService.id)(targetDate);
  };

  const handleSelecting = ({start, end}) => {
    setPlanningSelection({
      start: moment(start).format('HH:mm'),
      end: moment(end).format('HH:mm')
    });
    return true;
  };

  const handleSelectSlot = selection => {
    setPlanningSelection(null);
    newSlot(ownedService.id)(selection);
  };

  return (
    <Fragment>
      <CommonMetaTag/>
      <Fade in appear className="player-booking-calendar-shell partner-skywork-calendar-shell partner-planning-calendar-shell" style={{transition: 'opacity 0.30s linear', marginTop: 100, marginBottom: 100, '--current-time-label': `"${moment(currentTime).format('HH:mm')}"`, '--planning-selection-start': `"${planningSelection ? planningSelection.start : ''}"`, '--planning-selection-end': `"${planningSelection ? planningSelection.end : ''}"`}}>
        <Row className="justify-content-center player-booking-calendar-layout">
          <div className="partner-planner-heading">
            <div>
              <span>{t('PARTNER_PLANNER')}</span>
              <h4>{ownedService.name} · {t('PLAN_CALENDAR')}</h4>
              <p>{ownedService.bookingPolicy}</p>
            </div>
            <div className="partner-planner-mode-switch">
              <Link className="active" to="/partner-service-calendar">
                <FaPen aria-hidden="true"/>
                <span>{t('PLAN')}<small>{t('CREATE_EDIT_SLOTS')}</small></span>
              </Link>
              <Link to="/partner-service-booking-calendar">
                <FaCalendarCheck aria-hidden="true"/>
                <span>{t('BOOK')}<small>{t('MANAGE_BOOKINGS')}</small></span>
              </Link>
            </div>
          </div>
          <div className="partner-planning-actions">
            <Button className="partner-planning-guide-button" size="sm" onClick={() => setShowPlanningGuide(true)}>
              <FaQuestionCircle aria-hidden="true"/> Guida pianificazione
            </Button>
          </div>
          <Calendar
            selectable
            localizer={localizer}
            defaultDate={new Date()}
            defaultView="week"
            view={view}
            onView={setView}
            min={minTime}
            max={maxTime}
            events={events}
            step={15}
            timeslots={4}
            views={['day', 'week']}
            style={{minHeight: 600}}
            eventPropGetter={eventStyleGetter}
            components={{event: CalendarEvent, toolbar: Toolbar}}
            culture={i18n.language ===  'en' ? 'en-GB' : i18n.language}
            messages={i18n.language === 'it' ? calendarMessages : null}
            onSelectEvent={editSlot}
            onSelecting={handleSelecting}
            onSelectSlot={handleSelectSlot}
            onNavigate={handleNavigate}
          />
        </Row>
      </Fade>
      <Modal show={isSlotModalVisible} onHide={hideSlotModal} dialogClassName="partner-slot-planning-modal">
        <Modal.Header closeButton>
          <Modal.Title>{t('EVENT')} {ownedFieldSlot === null || ownedFieldSlot.id === null ? "("+t('NEW')+")" : "("+t('CHANGE')+")"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ServiceSlot slot={ownedFieldSlot} save={saveSlot} remove={values => onRemove(values)}/>
        </Modal.Body>
        <Modal.Footer>
          {/*
            ownedFieldSlot === null || ownedFieldSlot.id === null || ownedFieldSlot.state === 'unavailable' ?
              null :
              <Button variant="warning" onClick={onUpload(ownedFieldSlot.slotId)}>
                {t('VIDEO_UPLOAD')}
              </Button>
        */}
          <Button variant="primary" style={{marginLeft: 10}} onClick={hideSlotModal}>
            {t('CLOSE_BUTTON')}
          </Button>
        </Modal.Footer>
      </Modal>
      <PartnerPlanningGuideModal
        show={showPlanningGuide}
        onHide={() => setShowPlanningGuide(false)}
        includeVideoOptions={false}
      />
      <Dialog
        ref={el => {
          dialog = el;
        }}/>
    </Fragment>
  );
};

ServiceCalendar.propTypes = {
  isLogged: PropTypes.bool,
  profile: PropTypes.object,
  ownedService: PropTypes.object,
  ownedServices: PropTypes.array,
  ownedFieldSlots: PropTypes.array,
  ownedFieldSlot: PropTypes.object,
  isSlotModalVisible: PropTypes.bool,
  editSlot: PropTypes.func.isRequired,
  newSlot: PropTypes.func.isRequired,
  hideSlotModal: PropTypes.func.isRequired,
  saveSlot: PropTypes.func.isRequired,
  deleteSlot: PropTypes.func.isRequired,
  getSlotsByWeek: PropTypes.func.isRequired,
  selectService: PropTypes.func.isRequired
};

ServiceCalendar.defaultProps = {
  isLogged: false,
  ownedService: null,
  ownedServices: [],
  profile: null,
  ownedFieldSlots: [],
  ownedFieldSlot: null,
  isSlotModalVisible: false
};

export default ServiceCalendar;
